import { Util } from "@xt/xtbase";

const { Big, moment } = Util;

export const TRADING_TABS = ["spot", "futures", "ai"];

export function computePageTotal({ page, pageSize, itemCount, hasNext, totalCount }) {
  const apiTotal = Number(totalCount);
  if (Number.isFinite(apiTotal) && apiTotal > 0) return apiTotal;
  if (!itemCount) return 0;
  if (hasNext) return page * pageSize + 1;
  return (page - 1) * pageSize + itemCount;
}

export function resolveTradingTab(queryTab) {
  const tab = String(queryTab || "").toLowerCase();
  if (tab === "futures" || tab === "ai") return tab;
  return "spot";
}

export function formatTradingSymbol(symbol) {
  if (!symbol) return "--";
  const normalized = String(symbol).replace(/_cfd$/i, "");
  const parts = normalized.split("_").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].toUpperCase()}/${parts[1].toUpperCase()}`;
  }
  return normalized.toUpperCase();
}

export function extractBaseSymbolFromCfd(symbol) {
  const normalized = String(symbol || "").replace(/_cfd$/i, "");
  const parts = normalized.split("_").filter(Boolean);
  return (parts[0] || normalized).toUpperCase();
}

export function formatPnlRoe(pnl, roe) {
  const pnlNum = pnl && typeof pnl.toNumber === "function" ? pnl.toNumber() : Number(pnl);
  const roeNum = Number(roe);
  const safePnl = Number.isFinite(pnlNum) ? pnlNum : 0;
  const safeRoe = Number.isFinite(roeNum) ? roeNum : 0;
  const pnlSign = safePnl >= 0 ? "+" : "−";
  const roeSign = safeRoe >= 0 ? "+" : "−";
  const pnlAbs =
    pnl && typeof pnl.abs === "function"
      ? pnl.abs().toFixedCy(2)
      : Math.abs(safePnl).toFixed(2);
  return `${pnlSign}${pnlAbs} (${roeSign}${Math.abs(safeRoe).toFixed(2)}%)`;
}

export function formatPrice(value) {
  if (value == null || value === "") return "--";
  try {
    const n = Big(String(value).replace(/,/g, ""));
    if (n.gte(1000)) return n.toFixedCy(2);
    if (n.gte(1)) return n.toFixedCy(2);
    return n.toFixedCy(4);
  } catch {
    return String(value);
  }
}

export function formatAmount(value) {
  if (value == null || value === "") return "0";
  try {
    return Big(String(value).replace(/,/g, "")).toFixedCy();
  } catch {
    return String(value);
  }
}

export function formatOrderTime(time) {
  if (!time) return "--";
  try {
    return moment(time).formatMs();
  } catch {
    return "--";
  }
}

export function calcFillPercent(origQty, tradeBase) {
  try {
    const total = Big(String(origQty || 0).replace(/,/g, ""));
    const filled = Big(String(tradeBase || 0).replace(/,/g, ""));
    if (total.lte(0)) return "0";
    return filled.div(total).times(100).toFixed(0);
  } catch {
    return "0";
  }
}

export function tradePathFromSymbol(symbol) {
  const normalized = String(symbol || "")
    .replace(/_cfd$/i, "")
    .replace(/_/g, "")
    .toLowerCase();
  if (!normalized) return "/trade/btc_usdt";
  return `/trade/${normalized}`;
}

export function mapConvertOrderState(state) {
  const s = String(state || "").toUpperCase();
  if (s === "PENDING") return "processing";
  if (s === "SUCCEED") return "completed";
  if (s === "FAILED") return "failed";
  return "processing";
}

function toUpperDisplay(val, fallback = "--") {
  const raw = val != null && val !== "" ? val : fallback;
  if (raw == null || raw === "") return fallback;
  return String(raw).toUpperCase();
}

function formatConvertMaxNum(num) {
  if (num == null) return "--";
  if (Number.isNaN(Number(num))) return String(num);
  try {
    return Big(num).toFixedMaxCy();
  } catch {
    return String(num);
  }
}

/** 对齐 web-index bills/_domain/Convert.js */
export function mapConvertOrderRow(order) {
  const currencyDisplayName = toUpperDisplay(order.currencyDisplayName ?? order.currencyCode, "--");
  const cryptoCurrencyDisplayName = toUpperDisplay(
    order.cryptoCurrencyDisplayName ?? order.cryptoCurrencyCode,
    "USDT",
  );
  const sourceAmount = formatConvertMaxNum(order.sourceAmount);
  const targetAmount = formatConvertMaxNum(order.targetAmount);
  const exchangeRate = formatConvertMaxNum(order.exchangeRate);
  const totalFee = formatConvertMaxNum(order.totalFee);
  const rawFeeCurrency = order.feeCurrencyCode;
  const feeCurrencyCode =
    rawFeeCurrency != null && rawFeeCurrency !== "" ? String(rawFeeCurrency).toUpperCase() : "";
  const created = order.createdTime;
  const time =
    created && moment(created).isValid() ? moment(created).format("YYYY-MM-DD HH:mm:ss") : "--";

  return {
    id: String(order.id),
    time,
    typeDisplay: `${currencyDisplayName} → ${cryptoCurrencyDisplayName}`,
    sourceAmountDisplay: `${sourceAmount} ${currencyDisplayName}`,
    targetAmountDisplay: `${targetAmount} ${cryptoCurrencyDisplayName}`,
    exchangeRate,
    totalFeeDisplay: feeCurrencyCode ? `${totalFee} ${feeCurrencyCode}` : totalFee,
    state: order.state ?? "",
    statusKey: mapConvertOrderState(order.state),
  };
}

export function getConvertStateText(t, state) {
  const s = String(state || "").toUpperCase();
  if (s === "SUCCEED") return trFallback(t, "bills.convertStateSucceed", "Succeeded");
  if (s === "PENDING") return trFallback(t, "bills.convertStatePending", "Pending");
  if (s === "FAILED") return trFallback(t, "bills.convertStateFailed", "Failed");
  return state || "--";
}

export function futuresPathFromSymbol(symbol) {
  const normalized = String(symbol || "").trim().toLowerCase();
  if (!normalized) return "/futures/contract/btc_usdt_cfd";
  return normalized.endsWith("_cfd") ? `/futures/contract/${normalized}` : `/futures/contract/${normalized}_cfd`;
}

function trFallback(t, key, fallback) {
  if (!t || !key) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

export function mapCfdHistoryRow(position, t) {
  const quantity = Big(position.quantity || 0);
  const avgPrice = Big(position.avgPrice || 0);
  const closeAvgPrice = Big(position.closeAvgPrice || 0);
  const currentPrice = Big(position.currentPrice || 0);
  const margin = Big(position.margin || 0);
  const realizedPnl = Big(position.realizedPnl || 0);
  const isHolding = position.status === 1;
  const markPrice = isHolding ? currentPrice : closeAvgPrice;
  let pnl = realizedPnl;
  let roe = "0.00";

  if (isHolding && avgPrice.gt(0) && markPrice.gt(0) && quantity.gt(0)) {
    const priceDiff = position.side === 1 ? markPrice.minus(avgPrice) : avgPrice.minus(markPrice);
    pnl = priceDiff.times(quantity);
  }

  if (margin.gt(0)) {
    roe = pnl.div(margin).times(100).toFixed(2);
  }

  const sideNum = Number(position.side);
  const isLong = sideNum === 1;

  return {
    id: position.id || position.positionId || position.symbol,
    symbol: position.symbol,
    pair: formatTradingSymbol(position.symbol),
    side: isLong ? "long" : "short",
    sideLabel: isLong ? "Long" : "Short",
    leverage: position.leverage || 0,
    quantity: quantity.gt(0) ? quantity.toFixedCy() : "0",
    entry: avgPrice.gt(0) ? avgPrice.toFixedCy() : "--",
    mark: markPrice.gt(0) ? markPrice.toFixedCy() : "--",
    pnl,
    roe,
    status: isHolding ? trFallback(t, "cfd.holding", "Open") : trFallback(t, "cfd.closed", "Closed"),
  };
}
